/**
 * Finance glossary — single source of truth for what each P&L metric
 * means in this POS, the formula behind it, and where the underlying
 * data comes from. Wire this into any tile / column / chart that shows
 * money so users never have to guess "is this tax-inclusive?" or
 * "does Net Profit count COGS?".
 *
 * The Pakistani-SME ladder we standardise on:
 *
 *   Gross Sales   = SUM(orders.total_amount)            (tax-inclusive
 *                                                        — what the
 *                                                        customer paid)
 *   Net Sales     = Gross Sales − Sales Tax            (revenue we
 *                                                        actually earned;
 *                                                        gross already
 *                                                        reflects discounts)
 *   Gross Profit  = Net Sales − COGS                    (margin after
 *                                                        cost of goods)
 *   Net Profit    = Gross Profit − Operating Expenses   (no income tax
 *                                                        — Pakistan
 *                                                        SMEs handle
 *                                                        that elsewhere)
 *
 * Sales Tax is a LIABILITY owed to FBR/PRA — never counted as revenue
 * or profit. It rides Gross Sales into the till but is subtracted
 * before any downstream margin math.
 */

export type MetricKey =
  | 'gross_sales'
  | 'net_sales'
  | 'sales_tax'
  | 'discounts'
  | 'cogs'
  | 'gross_profit'
  | 'operating_expenses'
  | 'net_profit'
  | 'net_profit_after_refunds'
  | 'projected_margin'
  | 'projected_margin_pct'
  | 'cost_coverage_pct'
  | 'avg_ticket'
  | 'orders'
  | 'refunds_issued'
  | 'gross_sales_after_refunds'
  | 'credit_note_tax'
  | 'net_sales_tax'
  | 'tender_cash'
  | 'tender_card'
  | 'tender_online'

export interface MetricDefinition {
  /** Human-readable label rendered next to the value. */
  label: string
  /** Plain-language description shown in the tooltip. */
  summary: string
}

export const FINANCE_GLOSSARY: Record<MetricKey, MetricDefinition> = {
  gross_sales: {
    label: 'Gross sales',
    summary: 'Total cash collected from customers — tax-inclusive.',
  },
  net_sales: {
    label: 'Net sales',
    summary:
      'Revenue you actually earned after sales tax — discounts are already reflected in gross sales, so they are not subtracted again here.',
  },
  sales_tax: {
    label: 'Sales tax',
    summary: 'Tax collected on behalf of FBR/PRA. Owed to the government, not yours to keep.',
  },
  discounts: {
    label: 'Discounts',
    summary: 'Total discount value applied across completed orders.',
  },
  cogs: {
    label: 'COGS',
    summary: 'Cost of Goods Sold — the projected cost of items at the time of sale.',
  },
  gross_profit: {
    label: 'Gross profit',
    summary: 'What you keep after covering ingredient cost — before fixed expenses.',
  },
  operating_expenses: {
    label: 'Operating expenses',
    summary: 'All overhead recorded in the Expenses ledger — rent, salaries, utilities, etc.',
  },
  net_profit: {
    label: 'Net profit',
    summary: "What's actually left for the business after every cost. Before refunds.",
  },
  net_profit_after_refunds: {
    label: 'Net profit (after refunds)',
    summary:
      "Net profit minus refunds returned to customers in the same window. The food/service for a refunded order was still produced, so its cost is real — but the cash went back out the till. Read this number when the period has any refunds.",
  },
  projected_margin: {
    label: 'Projected margin',
    summary: 'Profit you projected based on per-item cost assumptions.',
  },
  projected_margin_pct: {
    label: 'Margin %',
    summary: 'Projected margin as a share of net sales.',
  },
  cost_coverage_pct: {
    label: 'Cost coverage',
    summary:
      'Share of net sales whose products had a cost configured. Below 50% means margin numbers should be read with caution.',
  },
  avg_ticket: {
    label: 'Avg order value',
    summary: 'Average order value for the period (tax-inclusive gross per completed check).',
  },
  orders: {
    label: 'Orders',
    summary: 'Number of completed orders in the period.',
  },
  refunds_issued: {
    label: 'Refunds issued',
    summary:
      'Total refunds returned to customers in this period — sum of the credit-note refund payments, dated by when staff processed them (business timezone).',
  },
  gross_sales_after_refunds: {
    label: 'Gross sales after refunds',
    summary: 'Tax-inclusive gross sales from completed orders minus issued refunds in the same window.',
  },
  credit_note_tax: {
    label: 'Credit-note tax reversed',
    summary:
      'Output VAT magnitude reversed via FBR credit notes posted in the period. Non-fiscal refunds are excluded — they never created a PRA liability.',
  },
  net_sales_tax: {
    label: 'Sales tax (net)',
    summary:
      'Output VAT after credit-note reversals — the figure to compare against your PRA monthly portal filing.',
  },
  tender_cash: {
    label: 'Cash',
    summary: 'Total received in physical cash.',
  },
  tender_card: {
    label: 'Card',
    summary: 'Total received via card terminal (debit + credit).',
  },
  tender_online: {
    label: 'Online',
    summary: 'Total received via bank transfer or digital wallet.',
  },
}

/** Convenience lookup; returns undefined for unknown keys. */
export function metricDefinition(key: MetricKey | string): MetricDefinition | undefined {
  return FINANCE_GLOSSARY[key as MetricKey]
}
